import React, { useEffect, useState } from "react";
import { IoMailOutline } from "react-icons/io5";
import { LuImagePlus, LuMapPin } from "react-icons/lu";
import { GoPencil } from "react-icons/go";
import profileImg from "../../../../assets/images/blank.webp";
import CompanyLayout from "../../../layout/CompanyLayout";
import { TbWorld } from "react-icons/tb";
import { FiPlus } from "react-icons/fi";
import CompanyDetailsPopup from "./CompanyDetailsPopup";
import { getCurrentUser, updateProfile } from "../../../../api/user.api";
import { getCompanyProfileData } from "../../../../api/company.api";
import { FaPhone } from "react-icons/fa6";
import { baseURL } from "../../../../api/api";
import { FaUserCircle } from "react-icons/fa";
import { MoonLoader } from "react-spinners"; // Import MoonLoader

function CompanyProfile() {
  const [updateCompanyPopupOpen, setUpdateCompanyPopupOpen] = useState(false);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [profileDetails, setProfileDetails] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true); // Track loading state for user info
  const [loadingCompanyInfo, setLoadingCompanyInfo] = useState(true); // Track loading state for company info

  const fetchCompanyProfile = () => {
    setLoadingUserInfo(true); // Start loading user info
    getCurrentUser()
      .then((res) => {
        setProfileDetails(res?.data?.user);
        getCompanyProfileData(res.data?.user?.companyId)
          .then((res) => {
            setCompanyDetails(res?.data?.company);
            setLoadingCompanyInfo(false); // Stop loading company info
          })
          .catch((error) => {
            console.error("Error fetching company data:", error);
            setLoadingCompanyInfo(false); // Stop loading on error
          });
        setLoadingUserInfo(false); // Stop loading user info
      })
      .catch((error) => {
        console.error("Error fetching user data:", error);
        setLoadingUserInfo(false); // Stop loading on error
      });
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const handleFileChange = (e) => {
    if (!e.target.files.length) {
      return;
    }
    const profileImg = e.target.files[0];
    setSelectedFile(profileImg);
    e.preventDefault();
    const formData = new FormData();
    formData.append("profileImage", profileImg);
    updateProfile(formData)
      .then((res) => {
        setCompanyDetails((prevOwnerDetails) => ({
          ...prevOwnerDetails,
          profileImage: res?.data?.user?.profileImage,
        }));
        fetchCompanyProfile();
      })
      .catch((error) => {
        console.error("Error uploading image:", error);
      });
  };

  return (
    <>
      <CompanyDetailsPopup
        open={updateCompanyPopupOpen}
        setOpen={setUpdateCompanyPopupOpen}
        companyDetails={companyDetails}
        mode={companyDetails ? "edit" : "add"}
        fetchCompanyProfile={fetchCompanyProfile}
      />
      <CompanyLayout title="Profile" profileDetails={profileDetails}>
        <div className="">
          <div className="lg:mx-auto md:px-1 lg:px-4">
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 lg:grid-cols-12 md:grid-cols-12">
              <div className="bg-white col-span-12 xl:col-span-4">
                <div className="rounded-md p-5">
                  <div className="flex items-center justify-between mb-2.5">
                    <h2 className="text-xl text-gray-500 text-opacity-80 font-semibold">
                      Your Information
                    </h2>
                  </div>
                  <div className="h-80 bg-gray-100 rounded-md px-4 py-5">
                    {loadingUserInfo ? (
                      <div className="flex items-center justify-center h-full">
                        <MoonLoader color="black" size={40} />
                      </div>
                    ) : (
                      <>
                        <div className="mx-auto relative w-28 h-28 rounded-full">
                          {profileDetails?.profileImage ? (
                            <img
                              src={`${baseURL}/s3/images/${profileDetails?.profileImage}`}
                              alt="Profile"
                              className="rounded-full h-full w-full object-cover"
                            />
                          ) : (
                            <FaUserCircle className="text-gray-300 h-full w-full" />
                          )}
                          <label
                            htmlFor="profileImage"
                            className="absolute top-0 mx-auto block w-28 h-28 rounded-full overflow-hidden cursor-pointer group"
                          >
                            <input
                              type="file"
                              name="profileImage"
                              id="profileImage"
                              className="hidden"
                              onChange={handleFileChange}
                            />
                            <GoPencil className="absolute bottom-2 right-0 text-green-600 h-8 w-8 bg-green-100 px-2 rounded-full cursor-pointer" />
                          </label>
                        </div>
                        <h2 className="text-center font-semibold text-lg my-2">
                          {profileDetails?.firstName} {profileDetails?.lastName}
                        </h2>
                        <div className="flex items-center justify-start px-3 my-2">
                          <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center">
                            <IoMailOutline />
                          </div>
                          <p className="ml-3">{profileDetails?.email}</p>
                        </div>
                        {profileDetails && profileDetails.phone && (
                          <div className="flex items-center justify-start px-3 my-2">
                            <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center">
                              <FaPhone />
                            </div>
                            <p className="ml-3">{profileDetails?.phone}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="bg-white col-span-12 xl:col-span-8">
                {loadingCompanyInfo ? (
                  <div className="flex items-center justify-center h-full">
                    <MoonLoader color="black" size={40} />
                  </div>
                ) : companyDetails ? (
                  <div className="rounded-md p-5">
                    <div className="flex items-center justify-between mb-2.5">
                      <h2 className="text-xl text-gray-500 text-opacity-80 font-semibold">
                        Company Information
                      </h2>
                      <button
                        onClick={() => {
                          setUpdateCompanyPopupOpen(true);
                        }}
                        className="text-sm flex items-center justify-center bg-green-100 text-green-700 py-0.5 px-2 rounded-md"
                      >
                        <span className="font-medium mr-2">Edit</span>
                        <GoPencil />
                      </button>
                    </div>
                    <div className="bg-gray-100 rounded-md px-4 py-7">
                      <div className="items-center grid grid-cols-1 grid-cols-12 gap-4">
                        <div className="lg:col-span-3 col-span-12">
                          {companyDetails?.logo &&
                          companyDetails.logo !== "null" ? (
                            <img
                              src={`${baseURL}/s3/images/${companyDetails.logo}`}
                              alt={companyDetails.logo}
                              className="mx-auto object-cover shrink-0 h-36 w-36 rounded-full"
                            />
                          ) : (
                            <div className="mx-auto bg-gray-50 h-36 w-36 rounded-full flex items-center justify-center">
                              <LuImagePlus className="text-gray-300 h-16 w-16 p-2" />
                            </div>
                          )}
                        </div>
                        <div className="lg:col-span-9 col-span-12">
                          <h2 className="px-3 font-semibold text-lg">
                            {companyDetails?.name}
                          </h2>
                          <div className="flex items-center justify-start px-3 my-2">
                            <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center">
                              <IoMailOutline className="text-gray-500 text-base" />
                            </div>
                            <p className="ml-3 text-gray-500 text-base">
                              {companyDetails?.email}
                            </p>
                          </div>
                          {companyDetails && companyDetails?.phone && (
                            <div className="flex items-center justify-start px-3 my-2">
                              <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center">
                                <FaPhone className="text-gray-500 text-base" />
                              </div>
                              <p className="ml-3 text-gray-500 text-base">
                                {companyDetails?.phone}
                              </p>
                            </div>
                          )}
                          <div className="flex items-center justify-start px-3 my-2">
                            <div className="bg-gray-200 rounded-full h-8 w-8 flex items-center justify-center">
                              <LuMapPin className="text-gray-500 text-base" />
                            </div>
                            <p className="ml-3 text-gray-500 text-base">
                              {companyDetails?.address}
                            </p>
                          </div>
                          <div className="px-3">
                            <h2 className="font-semibold text-lg py-1">
                              About us
                            </h2>
                            <p className="text-gray-500 text-base">
                              {companyDetails?.description}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-md p-5">
                    <h2 className="text-xl text-gray-500 text-opacity-80 font-semibold mb-3 ">
                      Company Profile Details
                    </h2>
                    <div className="flex-col flex items-center justify-center border-2 h-72 border-dashed border-gray-300 rounded-md">
                      <div className="h-20 w-20 border-2 border-gray-300 rounded-full flex items-center justify-center">
                        <button
                          onClick={() => {
                            setUpdateCompanyPopupOpen(true);
                          }}
                        >
                          <FiPlus className="text-5xl text-gray-400" />
                        </button>
                      </div>
                      <h2 className="text-center text-xl text-gray-400 text-opacity-80 font-medium my-4 ">
                        Add Information
                      </h2>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </CompanyLayout>
    </>
  );
}

export default CompanyProfile;
